import { Box, Button, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ChippyThread } from '../../shared/requests/chippyRequests';

interface ChippyThreadsPanelProps {
  threads: ChippyThread[];
  activeThreadId: string | null;
  isLoadingThreads: boolean;
  onStartNewChat: () => void;
  onSelectThread: (threadId: string) => void;
}

const ChippyThreadsPanel = ({
  threads,
  activeThreadId,
  isLoadingThreads,
  onStartNewChat,
  onSelectThread,
}: ChippyThreadsPanelProps) => {
  return (
    <Paper
      elevation={0}
      sx={{
        border: (theme) => `1px solid ${theme.palette.whiteScale.main}`,
        borderRadius: { xs: '16px', md: '22px' },
        background: (theme) =>
          `linear-gradient(180deg, ${theme.palette.whiteScaleLighter.main} 0%, ${theme.palette.whiteScaleLight.main} 100%)`,
        display: 'flex',
        flexDirection: 'column',
        minHeight: { xs: '200px', md: '100%' },
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: '0.9rem',
          py: '0.8rem',
          borderBottom: (theme) => `1px solid ${theme.palette.whiteScale.main}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.4rem',
        }}
      >
        <Typography sx={{ fontWeight: 700, color: (theme) => theme.palette.primaryDarker.main }}>Threads</Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={onStartNewChat}
          sx={{
            textTransform: 'none',
            borderColor: (theme) => theme.palette.whiteScale.main,
            color: (theme) => theme.palette.primaryDarker.main,
          }}
        >
          New chat
        </Button>
      </Box>

      <Box sx={{ p: '0.55rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: 1 }}>
        {isLoadingThreads ? (
          <Typography sx={{ p: '0.5rem', color: (theme) => alpha(theme.palette.primaryDarker.main, 0.72) }}>
            Loading threads...
          </Typography>
        ) : null}

        {!isLoadingThreads && threads.length === 0 ? (
          <Typography sx={{ p: '0.5rem', color: (theme) => alpha(theme.palette.primaryDarker.main, 0.72) }}>
            No saved chats yet.
          </Typography>
        ) : null}

        {threads.map((thread) => {
          const isSelected = activeThreadId === thread._id;
          return (
            <Button
              key={thread._id}
              variant="text"
              onClick={() => onSelectThread(thread._id)}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                p: '0.55rem 0.65rem',
                borderRadius: '12px',
                border: (theme) =>
                  `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.whiteScale.main}`,
                backgroundColor: (theme) =>
                  isSelected
                    ? alpha(theme.palette.primary.main, 0.08)
                    : alpha(theme.palette.whiteScaleLighter.main, 0.5),
                color: (theme) => theme.palette.primaryDarker.main,
                '&:hover': {
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                },
              }}
            >
              <Box sx={{ textAlign: 'left', width: '100%' }}>
                <Typography sx={{ fontSize: '0.87rem', fontWeight: 600 }} noWrap>
                  {thread.title || 'New conversation'}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', opacity: 0.8 }} noWrap>
                  {thread.lastMessagePreview || thread.firstMessagePreview || 'No preview available'}
                </Typography>
              </Box>
            </Button>
          );
        })}
      </Box>
    </Paper>
  );
};

export default ChippyThreadsPanel;
